import React from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import Layout from "./Layout";
import Landing from "./Landing";
import NotFound from "./NotFound";
import Blogs from "./Blog";
import ErrorPage from "./Error";
import withProps from "./withProps";
import BlogPage from "../components/blogPage";
import PlatformLanding from "./PlatformLanding";
import BrandHostRouter from "./BrandHostRouter";
import BookingPage from "./BookingPage";
import OAuthCallback from "./OAuthCallback";
import OAuthStart from "./OAuthStart";
import PublicSite from "./PublicSite";
import StorefrontGate from "./StorefrontGate";
import { StorefrontLogin, StorefrontSignup, StorefrontAccount } from "./StorefrontAuth";
import {
  StorefrontShop,
  StorefrontProduct,
  StorefrontCart,
  StorefrontCheckout,
  StorefrontOrderConfirm,
  StorefrontWishlist,
  StorefrontTrackOrder,
  StorefrontOrderReceipt,
  StorefrontContact,
} from "./StorefrontShop";
import ProtectedRoute from "components/auth/ProtectedRoute";
import PageNotFound from "./PageNotFound";

import { Box } from '@mui/material';
import {
  MdDashboard,
  MdHome,
  MdLock,
  MdShoppingCart,
  MdReceiptLong,
  MdMail,
  MdToken,
} from 'react-icons/md';

// Dashboard layouts
import AdminLayout from 'dashboard/layouts/admin';
import PlatformAdminLayout from 'dashboard/layouts/platform';

// Brand-facing pages (the actual product UI)
import Pages from 'dashboard/pages';
import WebPage from 'dashboard/pages/webpage';
import { AccountBilling, AccountInvoice, MarketingPlaceholder } from 'dashboard/pages/brand/Placeholders';

// Auth pages
import ForgotPasswordCentered from 'dashboard/views/auth/forgotPassword/ForgotPasswordCentered.jsx';
import AcceptInvite from 'dashboard/views/auth/acceptInvite/AcceptInvite.jsx';

/**
 * Renders the generic content editor for whichever library type is in the
 * URL. Keeps /dashboard/webpage/<type> working for all 26 types — and for
 * every one added later — without a route entry each.
 */
function LibraryItemPage() {
  const { stage } = useParams();
  return <WebPage stage={stage} />;
}


// Tiny <Icon as={...} /> helper so route entries can keep using
// `<Icon as={MdHome} />` shorthand without each one wrapping a Box.
const Icon = ({ as: As, width = '20px', height = '20px', color = 'inherit', ...rest }) => (
  <Box component={As} sx={{ width, height, color }} {...rest} />
);

const routes = (props) => createBrowserRouter([
  // Public root — host dispatcher decides between platform landing,
  // ComingSoon, a single page-builder page, or the storefront SPA,
  // based on the resolved Host header.
  {
    path: "/",
    element: <BrandHostRouter />,
    errorElement: <PageNotFound />,
  },
  // Explicit path if anything still needs the bare platform landing
  // (e.g. preview from inside the dashboard).
  { path: "/platform", element: <PlatformLanding /> },
  // Public brand sites — /site/:brandRef[/:pageSlug]
  { path: "/site/:brandRef", element: <PublicSite /> },

  // Storefront customer auth — preview form and custom-domain bare paths
  { path: "/site/:brandRef/login", element: <StorefrontLogin /> },
  { path: "/site/:brandRef/signup", element: <StorefrontSignup /> },
  { path: "/site/:brandRef/account", element: <StorefrontAccount /> },
  { path: "/login", element: <StorefrontLogin /> },
  { path: "/signup", element: <StorefrontSignup /> },
  { path: "/account", element: <StorefrontAccount /> },

  // Storefront commerce
  // The /site/:brandRef/… forms are gated too. They are the platform's own
  // preview addresses, which the gate lets through on the platform host — but
  // they resolve on a brand's custom domain as well, and there they were a
  // way around the gate: jfbross.com/site/<id>/track served a working order
  // tracker to a brand that sells nothing.
  { path: "/site/:brandRef/shop", element: <StorefrontGate fallback={<PublicSite pageSlug='shop' />}><StorefrontShop /></StorefrontGate> },
  { path: "/site/:brandRef/shop/:productSlug", element: <StorefrontGate><StorefrontProduct /></StorefrontGate> },
  { path: "/site/:brandRef/cart", element: <StorefrontGate><StorefrontCart /></StorefrontGate> },
  { path: "/site/:brandRef/checkout", element: <StorefrontGate><StorefrontCheckout /></StorefrontGate> },
  { path: "/site/:brandRef/order/:orderId", element: <StorefrontOrderConfirm /> },
  { path: "/site/:brandRef/order/:orderId/receipt", element: <StorefrontOrderReceipt /> },
  { path: "/site/:brandRef/wishlist", element: <StorefrontGate><StorefrontWishlist /></StorefrontGate> },
  { path: "/site/:brandRef/track", element: <StorefrontGate><StorefrontTrackOrder /></StorefrontGate> },
  { path: "/site/:brandRef/contact", element: <StorefrontGate fallback={<PublicSite pageSlug='contact' />}><StorefrontContact /></StorefrontGate> },
  { path: "/shop", element: <StorefrontGate fallback={<PublicSite pageSlug='shop' />}><StorefrontShop /></StorefrontGate> },
  { path: "/shop/:productSlug", element: <StorefrontGate><StorefrontProduct /></StorefrontGate> },
  { path: "/cart", element: <StorefrontGate fallback={<PublicSite pageSlug='cart' />}><StorefrontCart /></StorefrontGate> },
  { path: "/checkout", element: <StorefrontGate fallback={<PublicSite pageSlug='checkout' />}><StorefrontCheckout /></StorefrontGate> },
  { path: "/order/:orderId", element: <StorefrontOrderConfirm /> },
  { path: "/order/:orderId/receipt", element: <StorefrontOrderReceipt /> },
  { path: "/wishlist", element: <StorefrontGate fallback={<PublicSite pageSlug='wishlist' />}><StorefrontWishlist /></StorefrontGate> },
  // Booking. Falls through to a page of that name when the brand takes no
  // bookings, the same as every other reserved path.
  { path: "/book", element: <BookingPage /> },
  { path: "/site/:brandRef/book", element: <BookingPage /> },
  // Every reserved storefront path falls through to the owner's own page of
  // that name when the brand has no shop.
  //
  // Only /track and /contact did. So a brand without a storefront that
  // published a page called "Shop" — put it in their menu, linked to it,
  // watched the API serve it — got "we couldn't find that page" at
  // /shop, because the name was taken by a storefront they do not have and
  // the gate refused before the page was ever looked up.
  //
  // The ordering still protects checkout: the gate only falls through when
  // the brand has NO shop, so a brand that does have one cannot have its cart
  // shadowed by a page named "cart".
  { path: "/track", element: <StorefrontGate fallback={<PublicSite pageSlug='track' />}><StorefrontTrackOrder /></StorefrontGate> },
  { path: "/contact", element: <StorefrontGate fallback={<PublicSite pageSlug='contact' />}><StorefrontContact /></StorefrontGate> },

  { path: "/site/:brandRef/:pageSlug", element: <PublicSite /> },
  { path: "/site/:brandRef/:pageSlug/:entrySlug", element: <PublicSite /> },

  // Bare paths on a brand's own domain. The brand comes from the Host header,
  // so these carry no :brandRef — acme.com/blog and acme.com/blog/first-post.
  //
  // They sit last among the public routes on purpose: every named storefront
  // path above (/shop, /cart, /contact…) is matched first, so a brand cannot
  // shadow checkout by naming a page "cart".
  { path: "/:pageSlug", element: <PublicSite /> },
  { path: "/:pageSlug/:entrySlug", element: <PublicSite /> },

  // Catch-all
  { path: "*", element: <PageNotFound /> },

  // OAuth callback routes — backend redirects users here after consent
  { path: "/google/login", element: <OAuthCallback provider="google" /> },
  { path: "/github/login", element: <OAuthCallback provider="github" /> },
  { path: "/facebook/login", element: <OAuthCallback provider="facebook" /> },
  { path: "/twitter/login", element: <OAuthCallback provider="twitter" /> },

  // OAuth start — anything that lands on `/auth/<provider>` on the
  // frontend bounces to the backend's matching endpoint to start the
  // consent flow. Catches old links + the Sign-in/Sign-up buttons.
  { path: "/auth/:provider", element: <OAuthStart /> },

  // Legacy brand landing pages (served by domain)
  {
    path: "/brand",
    name: "Brand Site",
    hide: true, // legacy public landing — not a dashboard nav item
    element: withProps(Layout, props),
    errorElement: <ErrorPage />,
    children: [
      { name: 'Home', index: true, element: withProps(Landing, props) },
      { name: 'blog', path: "blog/:slug", element: withProps(BlogPage, props) },
      { name: 'blogs', path: "blog", element: withProps(Blogs, props) },
      { path: "*", element: <NotFound /> },
    ],
  },

  // Brand setup — owner-facing admin pages (the things you configure once)
  {
    name: 'Brand Manager',
    path: 'dashboard/brand',
    element: <ProtectedRoute>{withProps(AdminLayout, props)}</ProtectedRoute>,
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    collapse: true,
    children: [
      { name: 'Overview', layout: '/dashboard/brand/', path: 'overview', element: <Pages.BrandPages.Overview /> },
      { name: 'Settings', layout: '/dashboard/brand/', path: 'settings', element: <Pages.BrandPages.BrandSetting /> },
      { name: 'Plan and billing', layout: '/dashboard/brand/', path: 'billing', element: <Pages.BrandPages.Billing /> },
      { name: 'Domains', layout: '/dashboard/brand/', path: 'domains', element: <Pages.BrandPages.Domains /> },
      { name: 'Payments', layout: '/dashboard/brand/', path: 'payments', element: <Pages.BrandPages.BrandPayments /> },
      { name: 'IAM', layout: '/dashboard/brand/', path: 'iam', element: <Pages.BrandPages.IAMComponent /> },
      { name: 'Billing', layout: '/dashboard/brand/', path: 'billing', element: <AccountBilling /> },
      { name: 'API keys', layout: '/dashboard/brand/', path: 'api-keys', element: <Pages.BrandPages.ApiKeys /> },
      { name: 'Webhooks', layout: '/dashboard/brand/', path: 'webhooks', element: <Pages.BrandPages.Webhooks /> },
      { name: 'Support', layout: '/dashboard/brand/', path: 'support', element: <Pages.BrandPages.Tickets /> },
      { name: 'Notifications', layout: '/dashboard/brand/', path: 'notifications', element: <Pages.BrandPages.Notifications /> },
      { name: 'Activity', layout: '/dashboard/brand/', path: 'activity', element: <Pages.BrandPages.Activity /> },
      // Legacy paths from the old flat brand-manager layout — kept so any
      // bookmark or external link still lands somewhere useful instead of
      // showing the 404 page. Hidden from the sidebar via `hide: true`.
      { layout: '/dashboard/brand/', path: 'commerce',     element: <Navigate to='/dashboard/ecommerce/commerce' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'orders',       element: <Navigate to='/dashboard/ecommerce/orders' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'audience',     element: <Navigate to='/dashboard/ecommerce/audience' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'promotions',   element: <Navigate to='/dashboard/ecommerce/promotions' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'reviews',      element: <Navigate to='/dashboard/ecommerce/reviews' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'low-stock',    element: <Navigate to='/dashboard/ecommerce/low-stock' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'inbox',        element: <Navigate to='/dashboard/ecommerce/inbox' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'marketing',    element: <Navigate to='/dashboard/ecommerce/marketing' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'nft',          element: <Navigate to='/dashboard/nft/studio' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'mail',         element: <Navigate to='/dashboard/mail/manager' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'accounting',   element: <Navigate to='/dashboard/accounting/home' replace />, hide: true },
      { layout: '/dashboard/brand/', path: 'invoice',      element: <Navigate to='/dashboard/accounting/invoice' replace />, hide: true },
    ],
  },

  // Ecommerce — catalog, customers, orders, marketing
  // Web page manager.
  //
  // Regrouped into the three layers the builder actually has, because a flat
  // list of twelve gave no clue that eleven of them were ingredients and one
  // was the kitchen: "Pages" is the Page → Section → Component builder, while
  // the rest are brand CONTENT (their own collections, filled in once) that
  // pages point at. Nesting says what each thing is by where it sits.
  //
  // The legacy `/dashboard/webpage/*` paths are unchanged, so existing links,
  // bookmarks and anything a brand has already filled in keep working — this
  // moves where they appear in the menu, nothing else.
  {
    name: 'Web page manager',
    path: 'dashboard/webpage',
    element: <ProtectedRoute>{withProps(AdminLayout, props)}</ProtectedRoute>,
    icon: <Icon as={MdDashboard} width="20px" height="20px" color="inherit" />,
    collapse: true,
    children: [
      { name: 'Pages', layout: '/dashboard/webpage/', path: 'pages', exact: false, element: <Pages.BrandPages.PageBuilder /> },
      {
        name: 'Global design',
        layout: '/dashboard/webpage/',
        path: 'global',
        exact: false,
        element: <Pages.BrandPages.GlobalDesign />,
      },
      // Content, not pages. The landing page is the way in — it shows all
      // eleven types with counts, so "what have I not filled in yet" is one
      // glance instead of eleven visits. The individual editors keep their
      // original URLs underneath.
      {
        name: 'Content library',
        layout: '/dashboard/webpage/',
        path: 'library',
        exact: false,
        element: <Pages.BrandPages.ContentLibrary />,
      },
      {
        name: 'Enquiries',
        layout: '/dashboard/webpage/',
        path: 'enquiries',
        exact: false,
        element: <Pages.BrandPages.Enquiries />,
      },
      // One dynamic route instead of an entry per content type.
      //
      // The library has 26 types and will grow; a route per type means every
      // new one needs a code change in two places and silently 404s if the
      // second is forgotten. The editor is generic — it takes the type as a
      // prop — so the URL segment can simply be that type.
      //
      // Pathless group so the children match directly under the parent:
      // these are React Router route objects, and giving the group a `path`
      // would nest them a level deeper and break /dashboard/webpage/blogs.
      {
        name: 'Content library items',
        hide: true,
        collapse: true,
        children: [
          { name: 'Library item', layout: '/dashboard/webpage/', path: ':stage', exact: false, hide: true, element: <LibraryItemPage /> },
        ],
      },
    ],
  },

  {
    name: 'Ecommerce',
    path: 'dashboard/ecommerce',
    element: <ProtectedRoute>{withProps(AdminLayout, props)}</ProtectedRoute>,
    icon: <Icon as={MdShoppingCart} width="20px" height="20px" color="inherit" />,
    collapse: true,
    children: [
      { name: 'Commerce', layout: '/dashboard/ecommerce/', path: 'commerce', element: <Pages.BrandPages.Ecommerce /> },
      { name: 'Orders', layout: '/dashboard/ecommerce/', path: 'orders', element: <Pages.BrandPages.Orders /> },
      { name: 'Audience', layout: '/dashboard/ecommerce/', path: 'audience', element: <Pages.BrandPages.Audience /> },
      { name: 'Promotions', layout: '/dashboard/ecommerce/', path: 'promotions', element: <Pages.BrandPages.Discounts /> },
      { name: 'Reviews', layout: '/dashboard/ecommerce/', path: 'reviews', element: <Pages.BrandPages.Reviews /> },
      { name: 'Low stock', layout: '/dashboard/ecommerce/', path: 'low-stock', element: <Pages.BrandPages.LowStock /> },
      { name: 'Bookings', layout: '/dashboard/ecommerce/', path: 'bookings', element: <Pages.BrandPages.Bookings /> },
      { name: 'Inbox', layout: '/dashboard/ecommerce/', path: 'inbox', element: <Pages.BrandPages.Inbox /> },
      { name: 'Marketing', layout: '/dashboard/ecommerce/', path: 'marketing', element: <MarketingPlaceholder /> },
    ],
  },

  // NFT — collection design, deploy, drops
  {
    name: 'NFT',
    path: 'dashboard/nft',
    element: <ProtectedRoute>{withProps(AdminLayout, props)}</ProtectedRoute>,
    icon: <Icon as={MdToken} width="20px" height="20px" color="inherit" />,
    collapse: true,
    children: [
      { name: 'NFT Studio', layout: '/dashboard/nft/', path: 'studio', element: <Pages.BrandPages.NftStudio /> },
    ],
  },

  // Mail — sender identity, templates, deliverability
  {
    name: 'Mail',
    path: 'dashboard/mail',
    element: <ProtectedRoute>{withProps(AdminLayout, props)}</ProtectedRoute>,
    icon: <Icon as={MdMail} width="20px" height="20px" color="inherit" />,
    collapse: true,
    children: [
      { name: 'Mail manager', layout: '/dashboard/mail/', path: 'manager', element: <Pages.BrandPages.MailManager /> },
    ],
  },

  // Accounting — tax rules, invoices, reports
  {
    name: 'Accounting',
    path: 'dashboard/accounting',
    element: <ProtectedRoute>{withProps(AdminLayout, props)}</ProtectedRoute>,
    icon: <Icon as={MdReceiptLong} width="20px" height="20px" color="inherit" />,
    collapse: true,
    children: [
      { name: 'Accounting', layout: '/dashboard/accounting/', path: 'home', element: <Pages.BrandPages.Accounting /> },
      { name: 'Invoice manager (legacy)', layout: '/dashboard/accounting/', path: 'invoice', element: <AccountInvoice />, hide: true },
    ],
  },

  // Platform Admin lives on its own top-level route `/admin/*` with a
  // completely separate layout (sidebar, topbar, design). Brand owners
  // never see it; the layout itself gates on `usePlatformAdmin` and
  // every backend endpoint is independently gated by
  // `requirePlatformAdmin`. Sub-routes (overview, users, brands, ...)
  // are owned by the platform layout's own <Routes>.
  {
    path: 'admin/*',
    element: <ProtectedRoute><PlatformAdminLayout /></ProtectedRoute>,
  },

  // Auth (reset password + accept-invite). Login/signup live in the
  // AuthModal on the landing page now — no dedicated /dashboard/auth/login
  // page. These two stay because they're entered via email links and
  // each has a unique flow that doesn't fit a modal.
  {
    name: 'Authentication',
    path: 'dashboard/auth',
    icon: <Icon as={MdLock} width="20px" height="20px" color="inherit" />,
    element: withProps(AdminLayout, { authentication: true, ...props }),
    collapse: true,
    hide: true,
    children: [
      { name: 'Reset Password', layout: '/dashboard/auth/', path: 'resetpassword', element: <ForgotPasswordCentered /> },
      { name: 'Accept Invite', layout: '/dashboard/auth/', path: 'accept-invite', element: <AcceptInvite /> },
    ],
  },
]);

export default routes;
